// 導演頁（這首歌）對 cue 的操作：拖 cue 換順序 / 對調、換成別的 cue、拿掉、拖 cue 編號改時間、拉轉場、改歌曲長度。
// 都是「放開就存」（存進後台，不是調整舞台的草稿），每一個操作都把反向的呼叫放進 OpsUndoStack → Ctrl+Z 可以退回（useAdjustHotkeys）。
// 後台失敗：不動畫面（呼叫端的畫面本來就是照後台回來的資料畫），跳白話錯誤，回 false。
// 權限：導演、專案的製作、全站製作（directorStage）；「用其他身分看」時 guard 擋住寫入（呼叫端傳 va.guard）。

import { useCallback, useMemo, useRef, useState } from 'react';
import type { OpsUndoStack } from '../../lib/adjust/undoStack';
import {
  patchSongStage, setSlotStageCrossfade, stageRearrangeCues, stageRemoveCue, stageReplaceCue, stageSwapCues,
  addStageLibraryCue, type StageOrder,
} from '../../lib/adjust/stageApi';
import { saveCueMoves } from '../../lib/adjust/cueMoveSave';
import { friendlyError } from '../../lib/songReview';
import { toast } from '../../lib/toast';

export interface UseCueOpsOptions {
  projectId: string;
  songId: string;
  ops: OpsUndoStack;
  /** 做完（或退回完）重讀這首歌的 cue、位置、歌（名字、順序、時間、轉場、長度才會更新）；要等讀完 */
  reload: () => Promise<void>;
  /** 預覽（用其他身分看）時擋住寫入 */
  guard: <A extends unknown[]>(fn: (...args: A) => void) => (...args: A) => void;
}

/** 做一件事的結果：成功 true；失敗已經跳了白話錯誤 */
export type OpResult = Promise<boolean>;

export function useCueOps(o: UseCueOpsOptions) {
  const latest = useRef(o);
  latest.current = o;
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  /** 一次只做一件事；失敗跳白話錯誤 */
  const run = useCallback(async (label: string, fn: () => Promise<(() => Promise<void>) | null>): OpResult => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    try {
      const undo = await fn();
      await latest.current.reload();
      if (undo) latest.current.ops.push(label, async () => { await undo(); await latest.current.reload(); });
      return true;
    } catch (e) {
      toast.error(`${label}沒有成功：${friendlyError(e)}`);
      try { await latest.current.reload(); } catch { /* 下次自動更新再讀 */ }
      return false;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  const api = useMemo(() => {
    const p = () => ({ projectId: latest.current.projectId, songId: latest.current.songId });
    return {
      /** 兩個 cue 對調（時間格留在原位） */
      swap: (cueIdA: string, cueIdB: string): OpResult => run('對調 cue', async () => {
        const { projectId, songId } = p();
        await stageSwapCues(projectId, songId, cueIdA, cueIdB);
        return async () => { await stageSwapCues(projectId, songId, cueIdA, cueIdB); };
      }),
      /** 插到兩個 cue 中間：orderedIds = 全部正式 cue 的新順序 */
      rearrange: (orderedIds: string[]): OpResult => run('調整 cue 順序', async () => {
        const { projectId, songId } = p();
        const r = await stageRearrangeCues(projectId, songId, orderedIds);
        return async () => { await stageRearrangeCues(projectId, songId, r.previousOrderedIds); };
      }),
      /** 換成 Cue 庫的另一個 cue（時間不變，只改這首歌） */
      replace: (cueId: string, libraryCueId: string): OpResult => run('換成別的 cue', async () => {
        const { projectId, songId } = p();
        const r = await stageReplaceCue(projectId, songId, cueId, libraryCueId);
        const prev = r.previousLibraryCueId;
        return prev ? async () => { await stageReplaceCue(projectId, songId, cueId, prev); } : null;
      }),
      /** 從這首歌拿掉（Cue 庫的 cue 不會被刪）；退回 = 用同樣的時間和轉場把它加回來（加回來的是新的一格） */
      remove: (cueId: string): OpResult => run('拿掉 cue', async () => {
        const { projectId, songId } = p();
        const r = await stageRemoveCue(projectId, songId, cueId);
        const g = r.removed;
        return async () => {
          await addStageLibraryCue(projectId, songId, {
            libraryCueId: g.libraryCueId, videoTimeSec: g.videoTimeSec, ...(g.crossfadeSeconds === null ? {} : { crossfadeSeconds: g.crossfadeSeconds }),
          });
        };
      }),
      /** 改一個或好幾個 cue 的時間（拖 cue 編號）；整次算一步復原 */
      moveTimes: (changes: { cueId: string; videoTimeSec: number; crossfadeSeconds?: number }[]): OpResult => run('改 cue 的時間', async () => {
        const { projectId, songId } = p();
        // 被夾住的 cue 轉場一起存（色塊跟著走）；整次算一步復原
        const saved = await saveCueMoves(projectId, songId, changes);
        return saved.undo;
      }),
      /**
       * 拉轉場條：這個 cue 的轉場秒數（只改這首歌，變成這首歌自己設的）。
       * prev = 改之前的（跟著 Cue 庫的退回時回到跟著 Cue 庫）
       */
      setTransition: (cueId: string, seconds: number, extraUndo?: () => void): OpResult => run('改轉場秒數', async () => {
        const { projectId, songId } = p();
        const r = await setSlotStageCrossfade(projectId, songId, cueId, seconds, { own: true });
        const prev = r.previous;
        if (!prev) return extraUndo ? async () => { extraUndo(); } : null;
        return async () => {
          if (prev.own) await setSlotStageCrossfade(projectId, songId, cueId, prev.crossfadeSeconds, { own: true });
          else await setSlotStageCrossfade(projectId, songId, cueId, prev.crossfadeSeconds, { own: false });
          extraUndo?.();   // 一起縮放過的每片時間（草稿）也退回，整次算一步
        };
      }),
      /** 歌曲長度（秒；null = 清掉，回到原本的算法）。prev = 改之前的 */
      setLength: (sec: number | null, prev: number | null): OpResult => run('改歌曲長度', async () => {
        const { projectId, songId } = p();
        await patchSongStage(projectId, songId, { lengthSec: sec });
        return async () => { await patchSongStage(projectId, songId, { lengthSec: prev }); };
      }),
      /** 起始位置 */
      setStart: (body: { startMode: 'prev' | 'library_cue' | 'defaults'; startLibraryCueId?: string | null }, prev: { startMode: 'prev' | 'library_cue' | 'defaults'; startLibraryCueId: string | null }): OpResult => run('改起始位置', async () => {
        const { projectId, songId } = p();
        await patchSongStage(projectId, songId, body);
        return async () => { await patchSongStage(projectId, songId, prev); };
      }),
    };
  }, [run]);

  /** 寫入的動作包一層 guard（預覽別人的畫面時擋住） */
  const guarded = useMemo(() => {
    const g = o.guard;
    const wrap = <A extends unknown[]>(fn: (...a: A) => OpResult) => (...a: A): OpResult => new Promise<boolean>(resolve => { g((...x: A) => { void fn(...x).then(resolve); })(...a); });
    return {
      swap: wrap(api.swap), rearrange: wrap(api.rearrange), replace: wrap(api.replace), remove: wrap(api.remove),
      moveTimes: wrap(api.moveTimes), setTransition: wrap(api.setTransition), setLength: wrap(api.setLength), setStart: wrap(api.setStart),
    };
  }, [api, o.guard]);

  return { busy, ...guarded };
}

export type CueOpsApi = ReturnType<typeof useCueOps>;
export type { StageOrder };
